import { useState, useEffect, useRef, useCallback } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import api, { getThreadId, getVisitorName, setVisitorName } from "@/lib/api";
import { toast } from "sonner";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [name, setName] = useState(getVisitorName());
  const [needsName, setNeedsName] = useState(!getVisitorName());
  const [tempName, setTempName] = useState("");
  const scrollRef = useRef(null);
  const threadId = getThreadId();

  const loadMessages = useCallback(async () => {
    try {
      const res = await api.get(`/chat/threads/${threadId}/messages`);
      setMessages(res.data || []);
    } catch (e) {
      console.warn("Failed to load chat messages:", e);
    }
  }, [threadId]);

  useEffect(() => {
    loadMessages();
    const t = setInterval(loadMessages, 5000);
    return () => clearInterval(t);
  }, [loadMessages]);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const handleSend = async (e) => {
    e?.preventDefault();
    if (!text.trim()) return;
    try {
      const res = await api.post("/chat/messages", {
        thread_id: threadId,
        name: name || "Guest",
        text: text.trim(),
      });
      setMessages((m) => [...m, res.data]);
      setText("");
    } catch (e) {
      toast.error("Couldn't send message. Please try again.");
    }
  };

  const handleSetName = (e) => {
    e.preventDefault();
    if (!tempName.trim()) return;
    setVisitorName(tempName.trim());
    setName(tempName.trim());
    setNeedsName(false);
  };

  const unreadAdmin = messages.filter((m) => m.sender === "admin").length;

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        data-testid="chat-widget-toggle"
        aria-label="Open chat"
        className={cn(
          "fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-lg",
          "flex items-center justify-center hover:bg-primary/90 transition-all hover:scale-105",
          "ring-2 ring-accent/30"
        )}
      >
        {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
        {!open && unreadAdmin > 0 && (
          <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-accent text-accent-foreground text-xs font-semibold flex items-center justify-center">
            {unreadAdmin}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed bottom-24 right-6 z-50 w-[360px] max-w-[calc(100vw-3rem)] bg-card border border-soft rounded-sm shadow-2xl overflow-hidden flex flex-col fade-in-up"
          style={{ maxHeight: "520px" }}
        >
          <div className="bg-primary text-primary-foreground p-4">
            <h3 className="font-serif text-lg">Talk with Us</h3>
            <p className="text-xs text-primary-foreground/70 mt-0.5">
              We typically reply within a few minutes during business hours.
            </p>
          </div>

          {needsName ? (
            <form onSubmit={handleSetName} className="p-5 space-y-3" data-testid="chat-name-form">
              <p className="text-sm text-muted-foreground">
                Hi friend! What should we call you?
              </p>
              <Input
                value={tempName}
                onChange={(e) => setTempName(e.target.value)}
                placeholder="Your name"
                data-testid="chat-name-input"
                autoFocus
              />
              <Button type="submit" className="w-full" data-testid="chat-name-submit">
                Start Chatting
              </Button>
            </form>
          ) : (
            <>
              <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto p-4 space-y-3 bg-background"
                style={{ minHeight: "240px", maxHeight: "320px" }}
                data-testid="chat-message-list"
              >
                {messages.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    Welcome, {name}! How can we serve you today?
                  </p>
                ) : (
                  messages.map((m) => (
                    <div
                      key={m.id}
                      className={cn(
                        "flex",
                        m.sender === "visitor" ? "justify-end" : "justify-start"
                      )}
                      data-testid={`chat-message-${m.sender}`}
                    >
                      <div
                        className={cn(
                          "max-w-[80%] px-3 py-2 rounded-sm text-sm",
                          m.sender === "visitor"
                            ? "bg-primary text-primary-foreground"
                            : "bg-secondary text-secondary-foreground"
                        )}
                      >
                        {m.sender === "admin" && (
                          <div className="text-[10px] uppercase tracking-wider text-accent font-semibold mb-0.5">
                            {m.name}
                          </div>
                        )}
                        <div>{m.text}</div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <form
                onSubmit={handleSend}
                className="border-t border-soft p-3 flex gap-2"
                data-testid="chat-send-form"
              >
                <Input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Type a message…"
                  data-testid="chat-message-input"
                  className="flex-1"
                />
                <Button
                  type="submit"
                  size="icon"
                  className="bg-accent text-accent-foreground hover:bg-accent/90"
                  data-testid="chat-send-button"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
}
